import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {FacebookIcon, getFacebookAuthToken} from "@khan/auth-facebook";
import type {LoginType} from "@khan/auth-shared";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";

import RequireReauth from "../require-reauth.tsx";

interface Props {
    gqlClient: ApolloGqlClient;
    facebookId: string | null | undefined;
    facebookPrimaryEmail: string | null | undefined;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    setWarningMessage: (message: string | null) => void;
    googlePrimaryEmail: string | null | undefined;
    appleId: string | null | undefined;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    linkAccountWith: (loginType: LoginType, token: string) => void;
}

export const LinkFacebookAccount: React.FC<Props> = ({
    facebookId,
    facebookPrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    googlePrimaryEmail,
    appleId,
    linkAccountWith,
    renderThirdPartyDisconnectButton,
}) => {
    const onConnectFacebook = () => {
        getFacebookAuthToken().then((token) => {
            linkAccountWith("facebook", token);
        });
    };

    const facebookEmailWithDisconnect = (
        <View style={styles.row}>
            <LabelMedium style={[styles.verticalCenter, styles.label]}>
                {/* Not all accounts with facebook ids have logged in with facebook since
                we started tracking facebook's primary email. We simply say "Connected"
                in this case. */}
                {facebookPrimaryEmail || t`Connected`}
            </LabelMedium>
            <Strut size={spacing.small_12} />
            {renderThirdPartyDisconnectButton("facebook")}
        </View>
    );

    const connectFacebookButton = (
        <RequireReauth
            identifier={identifier}
            showClasslinkAuthProvider={isClasslinkUser}
            onSuccess={onConnectFacebook}
            onFailure={() => {}}
            hasFacebookButton={false}
            // We check the other providers if the user has an
            // account registered with them
            hasGoogleButton={!!googlePrimaryEmail}
            hasAppleButton={!!appleId}
        >
            {(invokeReauth) => (
                <Button
                    onClick={() => {
                        setWarningMessage(null);
                        invokeReauth();
                    }}
                    size="small"
                >
                    {t`Connect Facebook`}
                </Button>
            )}
        </RequireReauth>
    );

    return (
        <View style={[styles.row, styles.thirdPartyButtonContainer]}>
            <View
                style={[
                    styles.row,
                    styles.thirdPartyButtonContainer,
                    styles.alignLabels,
                ]}
            >
                {/* Facebook icon needs a touch of pixel nudging down to
                    look visually centered vertically. */}
                <View
                    style={[
                        styles.verticalCenter,
                        styles.thirdPartyIcon,
                        {paddingTop: "1px"},
                    ]}
                >
                    <FacebookIcon ariaHidden={true} />
                </View>
                <LabelMedium style={[styles.verticalCenter, styles.label]}>
                    {doNotTranslate`Facebook`}
                </LabelMedium>
            </View>
            <Strut size={spacing.medium_16} />
            {facebookId ? facebookEmailWithDisconnect : connectFacebookButton}
        </View>
    );
};

const styles = StyleSheet.create({
    row: {
        alignItems: "center",
        flexDirection: "row",
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    thirdPartyIcon: {
        margin: spacing.xSmall_8,
        width: spacing.medium_16,
        height: spacing.medium_16,
    },
    thirdPartyButtonContainer: {
        height: spacing.xLarge_32,
        display: "flex",
        alignItems: "center",
    },
    alignLabels: {
        flex: "0 0 100px",
    },
    label: {
        color: color.offBlack,
    },
});

export default LinkFacebookAccount;
